import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import { appAuthAPI } from '../services/api';

/**
 * Complete a password reset from an emailed link.
 *
 * This page is why the emailed link used to do nothing: the backend has always
 * sent `/reset-password?token=...` and `POST /app-auth/password-reset/confirm/`
 * has always worked, but the frontend had no route at that path. The link
 * landed on an unknown route, fell through to the authenticated shell, and
 * bounced the visitor to /login with no explanation.
 *
 * Deliberately PUBLIC (outside PrivateRoute): someone resetting a password by
 * definition cannot log in first.
 */
function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(
        'This link is missing its reset token. Request a new password reset, and open the ' +
          'most recent email — an older link may have been superseded.'
      );
    }
  }, [token]);

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = token && password.length >= 12 && password === confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      await appAuthAPI.confirmPasswordReset(token, password);
      setDone(true);
      // Give the operator a moment to read the confirmation before moving on.
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const d = err.response?.data;
      setError(
        d?.error ||
          d?.new_password?.[0] ||
          d?.token?.[0] ||
          err.message ||
          'Could not reset the password. The link may have expired.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" gutterBottom>
            Choose a new password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Samba4 Web UI — this link is single-use and expires.
          </Typography>

          {done ? (
            <Alert icon={<CheckCircle fontSize="inherit" />} severity="success">
              Password updated. Taking you to the sign-in page…
            </Alert>
          ) : (
            <form onSubmit={submit}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Stack spacing={2}>
                <TextField
                  label="New password"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  autoFocus
                  disabled={!token || busy}
                  error={tooShort}
                  helperText={
                    tooShort ? 'Use at least 12 characters.' : 'At least 12 characters.'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShow((s) => !s)}
                          edge="end"
                          aria-label={show ? 'Hide password' : 'Show password'}
                        >
                          {show ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm new password"
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  fullWidth
                  disabled={!token || busy}
                  error={mismatch}
                  helperText={mismatch ? 'The two passwords do not match.' : ' '}
                />

                <Button type="submit" variant="contained" size="large" disabled={!canSubmit}>
                  {busy ? <CircularProgress size={24} /> : 'Set new password'}
                </Button>

                <Button onClick={() => navigate('/login')} disabled={busy}>
                  Back to sign in
                </Button>
              </Stack>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default ResetPassword;
